import React, { useState } from "react";
import ConnectionStatusBadge from "../ui/ConnectionStatusBadge";
import DemoCTA from "./DemoCTA";
import { useDemo } from "./DemoContext";
import { formatAccountSubtype } from "../../utils/formatters";

export default function DemoAccountManagement() {
    const {
        allAccounts,
        connectionHealth,
        hiddenAccountIds,
        toggleAccountHidden,
        refreshingLinkId,
        refreshConnection,
        getLinkRefreshTime
    } = useDemo();

    const [showHidden, setShowHidden] = useState(false);

    const links = connectionHealth.links;
    const accounts = allAccounts;

    const visibleAccounts = showHidden ? accounts : accounts.filter(a => !hiddenAccountIds.has(a.accountId));
    const hiddenCount = accounts.filter(a => hiddenAccountIds.has(a.accountId)).length;

    return (
        <div className="space-y-6">
            {/* Institution Links Section */}
            <div>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Linked Institutions</h2>
                        <p className="text-sm text-gray-500">Manage your bank connections</p>
                    </div>
                </div>

                <div className="space-y-3">
                    {links.map((link) => (
                        <div key={link.id} className="card p-4">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {link.institutionLogoUrl && (
                                            <img
                                                src={link.institutionLogoUrl}
                                                alt=""
                                                className="w-6 h-6 rounded"
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        )}
                                        <h3 className="font-medium text-gray-900">
                                            {link.institutionDisplayName || `Account Link #${link.id}`}
                                        </h3>
                                        <ConnectionStatusBadge status={link.connectionStatus} />
                                    </div>
                                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                        <span>Connected {new Date(link.lastSuccessfulSyncAt).toLocaleDateString()}</span>
                                        <span>Last synced {getLinkRefreshTime(link.id).toLocaleString()}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2 ml-4">
                                    <button
                                        onClick={() => refreshConnection(link.id)}
                                        disabled={refreshingLinkId === link.id}
                                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                                        title="Check connection health"
                                    >
                                        {refreshingLinkId === link.id ? (
                                            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                        )}
                                    </button>
                                    <button
                                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                        onClick={() => alert("Demo mode: Sign up to reconnect real accounts")}
                                    >
                                        Reconnect
                                    </button>
                                    <button
                                        className="px-3 py-1.5 text-sm bg-red-50 text-danger-500 rounded-lg hover:bg-red-100 transition-colors"
                                        onClick={() => alert("Demo mode: Sign up to manage real accounts")}
                                    >
                                        Unlink
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Individual Accounts Section */}
            <div>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Individual Accounts</h2>
                        <p className="text-sm text-gray-500">Hide accounts you don't want to track</p>
                    </div>
                    {hiddenCount > 0 && (
                        <button
                            onClick={() => setShowHidden(!showHidden)}
                            className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                        >
                            {showHidden ? "Hide" : "Show"} {hiddenCount} hidden
                        </button>
                    )}
                </div>

                <div className="space-y-2">
                    {visibleAccounts.map((account) => {
                        const isHidden = hiddenAccountIds.has(account.accountId);
                        return (
                            <div
                                key={account.accountId}
                                className={`card p-3 flex justify-between items-center ${isHidden ? "opacity-50" : ""}`}
                            >
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-900">{account.name}</span>
                                        <span className="text-xs text-gray-500">
                                            {formatAccountSubtype(account.subtype)}
                                        </span>
                                        {isHidden && (
                                            <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                                                Hidden
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        ****{account.mask || "----"}
                                    </p>
                                </div>
                                <button
                                    onClick={() => toggleAccountHidden(account.accountId)}
                                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                        isHidden
                                            ? "bg-accent-50 text-accent-600 hover:bg-accent-100"
                                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                    }`}
                                >
                                    {isHidden ? "Show" : "Hide"}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="text-sm text-gray-500 space-y-1">
                <p><strong>Reconnect:</strong> Re-authenticate if your bank requires it.</p>
                <p><strong>Unlink:</strong> Remove institution and all its accounts.</p>
                <p><strong>Hide:</strong> Exclude specific accounts from your dashboard and calculations.</p>
            </div>

            <DemoCTA variant="inline" />
        </div>
    );
}
